/**
 * The LaTeX view's Preamble and Blocks tabs (DESIGN-14, Ḥassān, 17 Oct): the preview's .tex read
 * as groups of preamble lines and as one slice of LaTeX per block. Pure functions over the text the
 * LaTeX tab already fetches (`GET /documents/{id}/preview/latex`), so nothing new is asked of the API
 * except the two markers around the author's own lines (`% lilia:your-preamble`).
 */
import type { LineRange } from "./block-line-map";

export const YOUR_PREAMBLE_START = "% lilia:your-preamble";
export const YOUR_PREAMBLE_END = "% lilia:end-your-preamble";

/** The groups the Preamble tab shows, in the order Lilia writes them. */
export type PreambleGroupKey =
  | "document" | "encoding" | "maths" | "typography" | "graphics" | "tables" | "lists"
  | "pageBreaks" | "code" | "algorithms" | "boxes" | "text" | "links" | "definitions" | "info" | "yours";

export interface PreambleRow {
  /** The LaTeX, one or several lines. */
  code: string;
  /** Number of lines in `code`. */
  lines: number;
  /** For a \usepackage line: the packages it loads (for the plain-words gloss). */
  packages?: string[];
  /** A titled run of definitions (a header comment from the file): shown folded. */
  title?: string;
  /** An author's line (between the markers). */
  yours?: boolean;
}

export interface PreambleGroup {
  key: PreambleGroupKey;
  rows: PreambleRow[];
  /** The file's NOTE comments under this group. */
  notes: string[];
}

export interface PreambleSummary {
  groups: PreambleGroup[];
  liliaLines: number;
  packages: number;
  yourLines: number;
}

/** Lilia's header comments → group. Matched on the start of the comment, case-insensitive. */
const HEADERS: [RegExp, PreambleGroupKey][] = [
  [/^encoding/i, "encoding"],
  [/^math/i, "maths"],
  [/^typography/i, "typography"],
  [/^graphics|^legacy graphics/i, "graphics"],
  [/^tables/i, "tables"],
  [/^lists/i, "lists"],
  [/^page-break|^pagination/i, "pageBreaks"],
  [/^code\b/i, "code"],
  [/^algorithm/i, "algorithms"],
  [/^callouts|^boxes/i, "boxes"],
  [/^text formatting|^comment package|^epigraph/i, "text"],
  [/^links/i, "links"],
  [/^document info/i, "info"],
];

/** Settings lines filed by what they serve, wherever the file puts them (Ḥassān: \graphicspath →
 * Graphics, \lstset → Code). Theorem declarations are one titled run under definitions. */
const PURPOSE: [RegExp, PreambleGroupKey | "theorems"][] = [
  [/^\\graphicspath\b/, "graphics"],
  [/^\\lst(set|definelanguage|definestyle)\b/, "code"],
  [/^\\hypersetup\b/, "links"],
  [/^\\(newtheorem|theoremstyle)\b/, "theorems"],
];

/** The title of the theorem-declarations run; the view shows it in the interface's language. */
export const THEOREMS_TITLE = "@theorems";

const ORDER: PreambleGroupKey[] = [
  "document", "encoding", "maths", "typography", "graphics", "tables", "lists", "pageBreaks",
  "code", "algorithms", "boxes", "text", "links", "definitions", "info", "yours",
];

const USEPACKAGE = /^\\usepackage\s*(?:\[[^\]]*\])?\s*\{([^}]*)\}/;
const RE_INFO = /^\\(title|author|date|thanks)\b/;

/** Splits the .tex at \begin{document}: the preamble lines, and the line where the body starts. */
export function splitPreamble(tex: string): { preamble: string[]; bodyStart: number } {
  const lines = tex.replace(/\r\n?/g, "\n").split("\n");
  const at = lines.findIndex((l) => /^\s*\\begin\{document\}/.test(l));
  return at < 0 ? { preamble: lines, bodyStart: lines.length } : { preamble: lines.slice(0, at), bodyStart: at };
}

/**
 * Reads Lilia's preamble into groups. A comment line after a blank line is a header: a known one
 * opens its group, any other one (Unicode shims, theorem environments, compatibility shims) starts a
 * titled run under "Lilia's definitions". Other comments are the file's own explanations and stay in
 * the Code tab, except NOTE comments, kept as notes. The author's lines (between the markers) come
 * last, one row each.
 */
export function parsePreamble(preambleLines: string[]): PreambleSummary {
  const groups = new Map<PreambleGroupKey, PreambleGroup>();
  const group = (key: PreambleGroupKey) => {
    let g = groups.get(key);
    if (!g) { g = { key, rows: [], notes: [] }; groups.set(key, g); }
    return g;
  };
  let current: PreambleGroupKey = "document";
  let run: PreambleRow | null = null;
  let inYours = false;
  let prevBlank = true;
  let liliaLines = 0;
  let openBraces = 0;
  let yourLines = 0;
  const pkgs = new Set<string>();

  const closeRun = () => { run = null; };

  for (const raw of preambleLines) {
    const line = raw.replace(/\s+$/, "");
    const t = line.trim();
    if (t === YOUR_PREAMBLE_START) { inYours = true; closeRun(); prevBlank = true; continue; }
    if (t === YOUR_PREAMBLE_END) { inYours = false; closeRun(); prevBlank = true; continue; }
    if (!t) { prevBlank = true; closeRun(); continue; }

    if (inYours) {
      if (t.startsWith("%")) { prevBlank = false; continue; }
      yourLines++;
      const m = t.match(USEPACKAGE);
      group("yours").rows.push({ code: line, lines: 1, yours: true, packages: m ? splitNames(m[1]) : undefined });
      prevBlank = false;
      continue;
    }

    if (t.startsWith("%")) {
      const text = t.replace(/^%+\s*/, "");
      if (/^NOTE\b/i.test(text)) {
        group(current).notes.push(text.replace(/^NOTE:?\s*/i, ""));
      } else if (prevBlank && text) {
        const known = HEADERS.find(([re]) => re.test(text));
        closeRun();
        if (known) current = known[1];
        else {
          current = "definitions";
          run = { code: "", lines: 0, title: text.replace(/[.:]\s.*$/, "").replace(/\s*\(.*$/, "").trim() };
          group("definitions").rows.push(run);
        }
      } else if (current === "pageBreaks" || current === "definitions") {
        // explanations inside a run stay with it in the Code tab only
      }
      prevBlank = false;
      continue;
    }

    liliaLines++;
    const purpose = PURPOSE.find(([re]) => re.test(t))?.[1];
    if (purpose === "theorems") {
      const defs = group("definitions");
      const last = defs.rows[defs.rows.length - 1];
      if (last?.title === THEOREMS_TITLE) { last.code += `\n${line}`; last.lines++; }
      else defs.rows.push({ code: line, lines: 1, title: THEOREMS_TITLE });
      run = null;
      prevBlank = false;
      continue;
    }
    if (purpose) {
      // A multi-line setting (\lstset{ … }) keeps its following lines while its braces are open.
      const row: PreambleRow = { code: line, lines: 1 };
      group(purpose).rows.push(row);
      run = row;
      openBraces = depth(line);
      prevBlank = false;
      continue;
    }
    if (run && openBraces > 0) {
      const r = run as PreambleRow;
      r.code += `\n${line}`; r.lines++;
      openBraces += depth(line);
      prevBlank = false;
      continue;
    }
    if (/^\\documentclass/.test(t)) {
      group("document").rows.push({ code: line, lines: 1 });
    } else if (RE_INFO.test(t)) {
      group("info").rows.push({ code: line, lines: 1 });
    } else if (current === "definitions" && run) {
      const r = run as PreambleRow;
      r.code = r.code ? `${r.code}\n${line}` : line;
      r.lines++;
    } else {
      const m = t.match(USEPACKAGE);
      if (m) {
        const names = splitNames(m[1]);
        names.forEach((n) => pkgs.add(n));
        closeRun();
        group(current).rows.push({ code: line, lines: 1, packages: names });
      } else if (run && !run.packages && !run.title) {
        run.code += `\n${line}`;
        run.lines++;
      } else {
        // Settings lines (\graphicspath, \lstset, \PassOptionsToPackage…) and multi-line
        // definitions: one row per run of consecutive lines.
        run = { code: line, lines: 1 };
        group(current).rows.push(run);
      }
    }
    prevBlank = false;
  }

  return {
    groups: ORDER.map((k) => groups.get(k)).filter((g): g is PreambleGroup => !!g && (g.rows.length > 0 || g.notes.length > 0)),
    liliaLines,
    packages: pkgs.size,
    yourLines,
  };
}

function depth(line: string): number {
  let d = 0;
  for (const ch of line.replace(/\\[{}]/g, "")) { if (ch === "{") d++; else if (ch === "}") d--; }
  return d;
}

function splitNames(s: string): string[] {
  return s.split(",").map((x) => x.trim()).filter(Boolean);
}

export interface BlockSlice {
  blockId: string;
  /** 1-based lines in the cleaned .tex, as the Code tab numbers them. */
  range: LineRange;
  code: string;
}

/**
 * One slice of LaTeX per block, in document order, from the cleaned .tex and its line map. A title
 * block (`titleIds`) writes \title, \author and \date in the preamble and nothing where it stands,
 * so its slice is those preamble lines (29 Oct 2026: its card was empty, its line the next block's).
 */
export function blockSlices(cleanedTex: string, lineMap: Map<string, LineRange>, titleIds?: ReadonlySet<string>): BlockSlice[] {
  const lines = cleanedTex.replace(/\r\n?/g, "\n").split("\n");
  return [...lineMap.entries()]
    .sort((a, b) => a[1].start - b[1].start)
    .map(([blockId, range]) => {
      const code = lines.slice(range.start - 1, range.end).join("\n").replace(/^\n+|\s+$/g, "");
      const title = !code && titleIds?.has(blockId) ? preambleTitle(lines) : null;
      return title ? { blockId, ...title } : { blockId, range, code };
    });
}

/** The preamble's \title, \author and \date commands (an \author may run over several lines). */
export function preambleTitle(lines: string[]): { range: LineRange; code: string } | null {
  const { bodyStart } = splitPreamble(lines.join("\n"));
  const picked: number[] = [];
  let depth = 0;
  for (let i = 0; i < bodyStart; i++) {
    if (depth === 0 && !/^\s*\\(title|author|date)\s*[[{]/.test(lines[i])) continue;
    picked.push(i);
    const opens = lines[i].match(/(?<!\\)\{/g)?.length ?? 0;
    const closes = lines[i].match(/(?<!\\)\}/g)?.length ?? 0;
    depth = Math.max(0, depth + opens - closes);
  }
  if (picked.length === 0) return null;
  return {
    range: { start: picked[0] + 1, end: picked[picked.length - 1] + 1 },
    code: picked.map((i) => lines[i]).join("\n"),
  };
}

/** Code over 8 lines folds to its first 6 (Ḥassān's rule); headings, paragraphs and equations never.
 * On a phone, over 6 lines folds to 4 (his phone round, 18 Oct). */
export const FOLD_OVER = 8;
export const FOLD_TO = 6;
export const PHONE_FOLD_OVER = 6;
export const PHONE_FOLD_TO = 4;
const NEVER_FOLD = new Set(["heading", "paragraph", "equation", "title"]);
export function foldsCode(blockType: string, code: string, over = FOLD_OVER): boolean {
  return !NEVER_FOLD.has(blockType) && code.split("\n").length > over;
}

/** A paragraph with no LaTeX command: the phone says so instead of repeating it in code. */
export function isPlainText(code: string): boolean {
  return !/\\[A-Za-z@]/.test(code);
}

/** Tables over 4 rows fold to 4 on a phone. */
export const PHONE_TABLE_ROWS = 4;

/** "3 citations · 1 cross-reference" facts for a paragraph's card footer. */
export function referenceFacts(code: string): { citations: number; references: number } {
  const cites = [...code.matchAll(/\\(?:cite|citep|citet|parencite|textcite|autocite)\*?(?:\[[^\]]*\])*\{([^}]*)\}/g)]
    .flatMap((m) => splitNames(m[1]));
  const refs = [...code.matchAll(/\\(?:ref|eqref|cref|Cref|autoref|pageref)\{[^}]*\}/g)];
  return { citations: new Set(cites).size, references: refs.length };
}
